// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.

import {
  ApprovePairingIntentRequestConfig,
  GenerateDeviceCodeRequestConfig,
  GetPairingIntentRequestConfig,
  ListPairedDevicesRequestConfig,
  type PairedDevice,
  type PairedDevicesList,
  type PairingIntent,
  RevokeDeviceRequestConfig,
  type RevokedDevice,
  useStorytellClient,
} from "@storytell/client";
import { err, type Result } from "neverthrow";

export type { PairedDevice, PairingIntent, RevokedDevice } from "@storytell/client";

interface DevicesServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
}

export interface DevicesService {
  getPairingIntent(intentId: string): Promise<Result<PairingIntent, Error>>;
  approvePairingIntent(intentId: string): Promise<Result<PairingIntent, Error>>;
  generateCode(): Promise<Result<PairedDevice, Error>>;
  listDevices(): Promise<Result<PairedDevicesList, Error>>;
  revoke(deviceId: string): Promise<Result<RevokedDevice, Error>>;
}

const authNotReadyError = (): Error => new Error("Auth not ready");

export function createDevicesService(config: DevicesServiceConfig): DevicesService {
  const client = useStorytellClient();

  return {
    async getPairingIntent(intentId) {
      return client.fetch(GetPairingIntentRequestConfig, { intentId }, undefined);
    },
    async approvePairingIntent(intentId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = ApprovePairingIntentRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { intentId }, undefined);
    },
    async generateCode() {
      if (!config.authReady()) return err(authNotReadyError());
      const request = GenerateDeviceCodeRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, undefined, undefined);
    },
    async listDevices() {
      if (!config.authReady()) return err(authNotReadyError());
      const request = ListPairedDevicesRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, undefined, undefined);
    },
    async revoke(deviceId) {
      if (!config.authReady()) return err(authNotReadyError());
      const request = RevokeDeviceRequestConfig.withBearerToken(config.authToken());
      return client.fetch(request, { deviceId }, undefined);
    },
  };
}
